import React from 'react';
import { SL_PREMIUM } from '@surfline/web-common';
import MeterPremiumPaywall from '../bundles/margins/metering/MeterPremiumPaywall';
import MeterBottomSheetRegWall from '../bundles/margins/metering/MeterBottomSheetRegWall';

/**
 * Utility function for Metering Modals. This is a centralized location for all Meter related logic in backplane.
 * Depending on the meter state of a user this function determines which Modal to be rendered based on the treatment variant the user is in.
 * @param {import('@surfline/web-common').MeterState} meter
 * @param {import('@surfline/web-common').UserState} user
 * @param {string[]} entitlements
 * @return {JSX.Element | null}
 */
export const computeMeterModal = (meter, user, entitlements) => {
  const isPremium = entitlements?.includes(SL_PREMIUM);

  const isAnonymous = !user;

  if (!meter || isPremium) {
    return null;
  }

  const { treatmentVariant, meterRemaining } = meter;

  // Anonymous Sessions
  if (isAnonymous) {
    // If treatment is OFF then don't show the Modals
    if (treatmentVariant.match(/^(off|control)$/)) {
      return null;
    }
    // If the variant is surf_check or account_benefits then compute the surf check eligibility
    // Eligible for a surf check if meterRemaining > 0. Don't show the RegWall Modal.
    const isSurfCheckEligible = meterRemaining > 0;
    return isSurfCheckEligible ? null : <MeterBottomSheetRegWall />;
  }

  // If treatment is OFF then don't show the Modals
  if (treatmentVariant.match(/^(off|control)$/)) {
    return null;
  }
  // If the variant is surf_check or account_benefits then compute the surf check eligibility
  // Eligible for a surf check if meterRemaining > 0. Don't show the RegWall Modal.
  const isSurfCheckEligible = meterRemaining > 0;
  return isSurfCheckEligible ? null : <MeterPremiumPaywall />;
};

/**
 * This helper function computes the Limited Time Offer Eligibility for a Rate Limited User. Stubbing this function for now.
 * @param {string?} [accessToken]
 */
export const computeLimitedTimeOfferEligibility = (accessToken) => {
  /*
    Compute the Limited Time Offer Eligibility for a Rate Limited User based on the user's Meter state. We do not compute this for Anonymous Users.
    The results of this function should be stored as part of the meter Redux state so that the consuming Apps can use these as well.
    If Eligible then return :
    {
      limitedTimeOfferEligible: true,
      limitedTimeOfferDaysRemaining: X (days in int),
      limitedTimeOfferLink: '',
    }
    If Not eligible return :
    {
      limitedTimeOfferEligible: false,
      limitedTimeOfferDaysRemaining: null,
      limitedTimeOfferLink: null,
    }
  */
  if (!accessToken) {
    return {
      limitedTimeOfferEligible: false,
      limitedTimeOfferDaysRemaining: null,
      limitedTimeOfferLink: null,
    };
  }

  return {
    limitedTimeOfferEligible: true,
    limitedTimeOfferDaysRemaining: 10,
    limitedTimeOfferLink: '',
  };
};
